// src/utils/voiceRecorder.js
// One app-wide voice recorder, so a recording keeps going when the user
// switches screens (the note panel that started it unmounts, the mic doesn't).
// At Stop the audio goes to the panel that started it if that panel is still
// open, else to the panel whose Stop was pressed; with no panel open it stays in
// IndexedDB as an untranscribed recording that any voice panel can Retry.
// Cancel throws the audio away without sending it anywhere (e.g. no answer).
import { addChunk, deleteRec, notifyVoiceRecsChanged, putRec } from "./voiceStore";

let state = { rec: null, ownerId: null, ownerMounted: false };
let mediaRecorder = null;
let chunks = [];
// IndexedDB writes still in flight, so Cancel can delete after the last one lands.
const pendingWrites = new Set();
const listeners = new Set();
const owners = new Map(); // ownerId -> (rec, blob) => void

// Shared by every useVoiceNote: ids being transcribed (hidden from the
// untranscribed list) and failed blobs IndexedDB could not keep.
export const busyIds = new Set();
export const memoryRecs = new Map();

function emit(next) {
  state = { ...next, ownerMounted: !!next.ownerId && owners.has(next.ownerId) };
  listeners.forEach((fn) => fn());
}

export function getRecorderState() {
  return state;
}

export function subscribeRecorder(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function registerOwner(id, onStopped) {
  owners.set(id, onStopped);
  emit(state);
  return () => {
    if (owners.get(id) === onStopped) owners.delete(id);
    emit(state);
  };
}

function track(p) {
  pendingWrites.add(p);
  p.finally(() => pendingWrites.delete(p));
}

export async function startRecording(ownerId) {
  if (mediaRecorder) throw new Error("A recording is already in progress");
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : undefined;
  // 32 kbps is plenty for speech: ~100 min fits Whisper's 25 MB cap.
  let mr;
  try {
    mr = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 32000 });
  } catch {
    mr = new MediaRecorder(stream, { mimeType });
  }
  const now = Date.now();
  const rec = {
    id: `${now}-${Math.random().toString(36).slice(2, 8)}`,
    startedAt: now,
    updatedAt: now,
    mimeType: mr.mimeType || mimeType || "audio/webm",
  };
  chunks = [];
  await putRec(rec);
  let lastTouch = now;
  mr.ondataavailable = (e) => {
    if (!e.data || e.data.size === 0) return;
    chunks.push(e.data);
    track(addChunk(rec.id, e.data));
    if (Date.now() - lastTouch > 5000) {
      lastTouch = Date.now();
      track(putRec({ ...rec, updatedAt: lastTouch }));
    }
  };
  mr.start(1000);
  mediaRecorder = mr;
  emit({ rec, ownerId });
}

// Returns true when a panel took the audio for transcription; false means it
// was left as an untranscribed recording.
export async function stopRecording(stopperId) {
  const mr = mediaRecorder;
  if (!mr || !state.rec) return false;
  mediaRecorder = null;
  await new Promise((resolve) => {
    mr.onstop = resolve;
    try { mr.stop(); } catch { resolve(); }
  });
  mr.stream?.getTracks().forEach((t) => t.stop());

  const endedAt = Date.now();
  const rec = { ...state.rec, endedAt, updatedAt: endedAt };
  const blob = new Blob(chunks, { type: rec.mimeType || mr.mimeType || "audio/webm" });
  chunks = [];
  rec.size = blob.size;
  const handler = owners.get(state.ownerId) || (stopperId ? owners.get(stopperId) : null);
  // Mark busy before the state clears so a refresh in between can't list it.
  busyIds.add(rec.id);
  emit({ rec: null, ownerId: null });
  const saved = await putRec(rec);
  if (!saved && blob.size > 0) memoryRecs.set(rec.id, { rec, blob });
  busyIds.delete(rec.id);
  if (handler) {
    handler(rec, blob);
    return true;
  }
  notifyVoiceRecsChanged();
  return false;
}

// Stops the mic and deletes the audio: nothing is transcribed or kept.
export async function cancelRecording() {
  const mr = mediaRecorder;
  if (!mr || !state.rec) return;
  const { id } = state.rec;
  mediaRecorder = null;
  mr.ondataavailable = null;
  await new Promise((resolve) => {
    mr.onstop = resolve;
    try { mr.stop(); } catch { resolve(); }
  });
  mr.stream?.getTracks().forEach((t) => t.stop());
  chunks = [];
  busyIds.add(id);
  emit({ rec: null, ownerId: null });
  await Promise.allSettled([...pendingWrites]);
  await deleteRec(id);
  busyIds.delete(id);
}
